<template>
    <div class="data-table">
        <!--Table title-->
        <div class="table-title" v-if="tableTitle || configColumn">
            <slot name="title-prepend"></slot>
            <span v-if="tableTitle && tableTitle !== ''">{{ tableTitle }}</span>
            <template v-if="configColumn">
                <v-btn @click="showDialogColumns" flat icon small color="primary">
                    <v-icon>mdi-cog</v-icon>
                </v-btn>
                <!--Dialog config field in table-->
                <v-dialog
                    no-click-animation
                    v-model="dialogColumns"
                    max-width="250"
                >
                    <div class="DialogColumns">
                        <!--Header dialog-->
                        <div class="dialog-header">
                            <div>{{ tableTitle || 'Configs' }}</div>
                            <v-btn small icon @click="dialogColumns = false">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </div>
                        <!--Content dialog-->
                        <div class="fci-scrollbar">
                            <template v-for="column in headers">
                                <div class="item-config" :key="column.value">
                                    <v-checkbox
                                        v-model="columnsModel"
                                        color="#0052CC"
                                        :value="column.value"
                                        @change="toggleColumns(column.value)"
                                    ></v-checkbox>
                                    <div style="margin-left: 12px; font-size: 14px">{{ column.text }}</div>
                                </div>
                            </template>
                        </div>
                    </div>
                </v-dialog>
            </template>
            <slot name="title-append"></slot>
        </div>
        <!--Table content-->
        <template v-if="defaultHeader.length">
            <v-data-table
                v-model="selected"
                :select-all="selectable"
                :headers="defaultHeader"
                :items="getItems"
                :class="{'data-table-mobile': $utils.isMobile() && !keepDesktopLayout}"
                :hide-headers="$utils.isMobile() && !keepDesktopLayout"
                :hide-footer="true"
                :disable-initial-sort="true"
                hide-actions
            >
                <template v-slot:headers="props">
                    <tr>
                        <th v-if="selectable">
                            <div class="flexbox">
                                <v-checkbox
                                    :input-value="props.all"
                                    :indeterminate="props.indeterminate"
                                    color="primary"
                                    hide-details
                                    @click.stop="toggleAllCheckbox"
                                ></v-checkbox>
                            </div>
                        </th>
                        <th
                            v-for="header in props.headers"
                            :key="header.value"
                            :class="[
                                'column',
                                header.sortable ? 'sortable' : '',
                                sortDirection ? 'asc' : 'desc',
                                sortBy && header.value === sortBy ? 'active' : '',
                                `text-${header.textAlign || 'left'}`
                            ]"
                            @click="updateSort(header.value, sortDirection, header.sortable)"
                        >
                            {{ header.text }}
                            <template v-if="header.sortable">
                                <v-icon small v-if="sortDirection === 'asc'">arrow_downward</v-icon>
                                <v-icon small v-if="sortDirection !== 'asc'">arrow_upward</v-icon>
                            </template>
                        </th>
                    </tr>
                </template>
                <template v-slot:items="props">
                    <tr v-if="$utils.isMobile() && !keepDesktopLayout"
                        class="mobile-row"
                        :class="rowClass"
                        @click="onRowClick(props.item)">
                        <td :colspan="defaultHeader.length" class="pa-0">
                            <div class="dtm-cell">
                                <div class="dtm-row" v-for="header in defaultHeader" :key="header.value">
                                    <div class="dtm-header">{{ header.text }}</div>
                                    <div>
                                        <template v-if="isTimeField(header)">
                                            {{ $filters.toHMS(props.item[header.value]) }}
                                        </template>
                                        <template v-else-if="header.value === 'status'">
                                            <span :style="{color: $utils.getStatusColor(props.item[header.value])}"
                                                  class="status-text">{{ props.item[header.value] }}</span>
                                        </template>
                                        <template v-else-if="header.type">
                                            <template v-if="header.type === 'custom'">
                                                <slot :name="header.id" :item="props.item"></slot>
                                            </template>
s                                            <template v-if="header.type === 'datetime'">
                                                {{ $filters.formatDateTime(props.item[header.value]) }}
                                            </template>
                                        </template>
                                        <template v-else>
                                            {{
                                                typeof (header.value) === 'function' ? header.value(props.item) : props.item[header.value]
                                            }}
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr v-else @click="onRowClick(props.item)" :class="rowClass">
                        <td v-if="selectable">
                            <v-checkbox
                                v-model="props.selected"
                                @change="emitSelectionData()"
                                color="primary"
                                hide-details
                            ></v-checkbox>
                        </td>
                        <td v-for="header in defaultHeader"
                            :key="header.value"
                            :class="header.class ? header.class : ''"
                        >
                            <template
                                v-if="isTimeField(header)">
                                {{ $filters.toHMS(props.item[header.value]) }}
                            </template>
                            <template v-else-if="header.value === 'status'">
                                <span :style="{color: $utils.getStatusColor(props.item[header.value])}"
                                      class="status-text">{{ $utils.convertSnakeToCapitalize(props.item[header.value]) }}</span>
                            </template>
                            <template v-else-if="header.type">
                                <template v-if="header.type === 'custom'">
                                    <slot :name="header.value" :item="props.item"></slot>
                                </template>
                                <template v-else-if="header.type === 'datetime'">
                                    {{ $filters.formatDateTime(props.item[header.value]) }}
                                </template>
                                <template v-else-if="header.type === 'date'">
                                    {{ formatDateCustom(props.item[header.value]) }}
                                </template>
                            </template>
                            <template v-else>
                                {{
                                    typeof (header.value) === 'function'
                                        ? header.value(props.item)
                                        : props.item[header.value]
                                }}
                            </template>
                        </td>
                    </tr>
                </template>
                <template slot="no-data">
                    <div class="text-xs-center" style="font-size: 14px !important;">{{ $f.tc('noDataAvailable') }}</div>
                </template>
            </v-data-table>
        </template>
        <!--Table footer-->
        <div v-if="!hidePager" class="pager-wrap">
            <div class="display-flex align-center items-per-page" v-if="!disablePerPage">
                    <span class="per-page-label">
                        {{ $f.tc('rowsPerPage') }}:
                    </span>
                <div class="select-field ml-8">
                    <v-select
                        hide-details outlined
                        v-model="currentPerPage"
                        :items="currentPerPageArray"
                        @change="perPageChange"
                        class="input-custom"
                    ></v-select>
                </div>
            </div>
            <div class="display-flex align-center total-items">
                <div>
                    <template v-if="totalItems === 0 || totalItems === '0'">
                        0 {{ $t('of') }} 0
                    </template>
                    <template v-else-if="currentPerPage >= totalItems">
                        1 - {{ totalItems + ` ${$t('of')} ` + totalItems }}
                    </template>
                    <template v-else>
                        {{ startRow + ' - ' + endRow + ` ${$t('of')} ` + totalItems }}
                    </template>
                </div>
                <div class="ml-6">{{ $t(nameItem) }}</div>
            </div>
            <div v-if="totalPage > 1" class="display-flex align-center">
                <div class="display-flex align-center select-page">
                    <div class="select-field">
                        <v-select
                            hide-details outlined
                            v-model="pager"
                            :items="listPage"
                            @change="pageChange"
                            class="input-custom"
                        ></v-select>
                    </div>
                    <div class="ml-8">
                        {{ ` ${$t('of')} ` + listPage[listPage.length - 1] + ` ${$t('pages')}` }}
                    </div>
                </div>
                <div class="btn-prev" @click="prevPage">
                    <img src="/img/prev.svg" alt="icon_prev">
                </div>
                <div class="btn-next" @click="nextPage">
                    <img src="/img/next.svg" alt="icon_next">
                </div>
            </div>
        </div>
        <div v-if="selected.length" class="selection-actions elevation-24"
             :class="toggleNav ? 'nav-opened' : 'nav-closed'">
            <div class="flexbox flex-justify-content-center">
                <div class="remove-selection">
                    <v-btn small color="gray" dark outline @click="selected = []">
                        <span>selected: {{ selected.length }}</span>
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
                <div class="selection-actions-slot">
                    <slot name="selection-actions"></slot>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import {mapState} from 'vuex'

export default {
    name: 'DataTableCustom',
    props: {
        tableTitle: String,
        configColumn: Boolean,
        headers: Array,
        items: Array,
        rowClass: {
            type: String,
            default: ''
        },
        sortBy: String,
        sortDirection: String,
        totalItems: {
            type: Number,
            default: 0
        },
        totalPage: {
            type: Number,
            default: 1
        },
        currentPage: {
            type: Number,
            default: 1
        },
        disablePerPage: Boolean,
        itemsPerPage: Number,
        perPageArray: Array,
        totalVisible: {
            type: Number,
            default: 7
        },
        hidePager: Boolean,
        addIndex: Boolean,
        keepDesktopLayout: Boolean,
        selectable: Boolean,
        selectedProp: Array,
        nameItem: {
            type: String,
            default: 'records'
        }
    },
    data() {
        return {
            pager: this.currentPage,
            currentPerPage: this.itemsPerPage || this.$const.itemPerPage,
            currentPerPageArray: this.perPageArray || this.$const.perPageArray,
            dialogColumns: false,
            columnsModel: [],
            selected: []
        }
    },
    computed: {
        ...mapState({
            toggleNav: state => state.navigation
        }),
        defaultHeader() {
            let headersTMP = []
            if (this.addIndex) {
                headersTMP.push({
                    text: 'No',
                    value: 'rowIndex',
                    sortable: false
                })
            }
            if (this.columnsModel) {
                for (let i = 0; i < this.headers.length; i++) {
                    let found = this.$lodash.find(this.columnsModel, value => value === this.headers[i].value)
                    if (found) {
                        headersTMP.push(this.headers[i])
                    }
                }
            } else {
                for (let i = 0; i < this.headers.length; i++) {
                    if (!this.headers[i]['invisible']) {
                        headersTMP.push(this.headers[i])
                    }
                }
            }

            return headersTMP
        },
        columnsModelTMP() {
            let columnsModelTMP = []
            if (this.addIndex) {
                columnsModelTMP.push('rowIndex')
            }
            for (let i = 0; i < this.headers.length; i++) {
                if (!this.headers[i]['invisible']) {
                    columnsModelTMP.push(this.headers[i].value)
                }
            }
            return columnsModelTMP
        },
        getItems() {
            if (this.addIndex && this.items.length) {
                this.items.map((item, idx) => {
                    this.$set(item, 'rowIndex', this.startRow + idx)
                })
            }
            return this.items
        },
        startRow() {
            return (this.currentPerPage * (this.currentPage - 1)) + 1
        },
        endRow() {
            return this.currentPerPage * this.currentPage >= this.totalItems ? this.totalItems : this.currentPerPage * this.currentPage
        },
        listPage() {
            let pages = []
            for (let i = 1; i <= this.totalPage; i++) {
                pages.push(i)
            }
            return pages
        }
    },
    methods: {
        onRowClick(rowData) {
            this.$emit('row-click', rowData)
        },
        pageChange() {
            this.$emit('page-change', this.pager)
        },
        perPageChange() {
            this.$emit('item-per-page-change', this.currentPerPage)
        },
        updateSort(key, direction, sortable) {
            if (!sortable) return
            this.$emit('sort-change', {
                value: key,
                direction: direction
            })
        },
        showDialogColumns() {
            this.dialogColumns = true
        },
        toggleColumns(column) {
            if (this.columnsModel.includes(column)) {
                this.headers.map(header => {
                    if (header.value === column) {
                        this.$set(header, 'invisible', false)
                    }
                })
            } else {
                this.headers.map(header => {
                    if (header.value === column) {
                        this.$set(header, 'invisible', true)
                    }
                })
            }
        },
        toggleAllCheckbox() {
            if (this.selected.length) this.selected = []
            else this.selected = this.items.slice()
            this.emitSelectionData()
        },
        emitSelectionData() {
            this.$nextTick().then(() => {
                this.$emit('selection-data', this.selected)
            })
        },
        isTimeField(header) {
            const timeFieldList = [
                'duration',
                'avg_call_time',
                'total_call_time',
                'call_transfer_duration'
            ]
            return timeFieldList.includes(header.value)
        },
        formatDateCustom(date) {
            if (this.$filters.formatDate(date) === 'Invalid date') {
                return date
            } else {
                return this.$filters.formatDate(date)
            }
        },
        prevPage () {
            if (this.pager > 1) {
                this.pager--
            }
            this.pageChange()
        },
        nextPage () {
            if (this.pager < this.totalPage) {
                this.pager++
            }
            this.pageChange()
        },
    },
    watch: {
        currentPage() {
            this.pager = this.currentPage
        },
        selectedProp(value) {
            this.selected = [...value]
        },
        itemsPerPage(val) {
            this.currentPerPage = val
        },
        headers: {
            immediate: true,
            handler: function (newHeaders, oldHeaders) {
                if (newHeaders && oldHeaders && oldHeaders.length === newHeaders.length) {
                    for (let i = 0; i < newHeaders.length; i++) {
                        newHeaders[i].invisible = oldHeaders[i].invisible
                    }
                }
                this.columnsModel = [...this.columnsModelTMP]
            }
        }
    }
}
</script>
<style lang="scss" scoped>
// style table
.table-title {
    background: #F4F7FE;
    border-radius: 0;
    border-bottom: 1px solid #DADFEC;
}

.data-table {
    border: 1px solid #DADFEC;

    .nowrap {
        white-space: nowrap;
    }
}

.pager-wrap {
    border: none;
    padding: 0;
    height: 40px;
    display: flex;
    align-items: center;
    background: #F4F7FE;
    font-size: 14px;
    color: #8A97AA;

    ::v-deep .v-text-field .v-input__append-inner {
        padding-left: 0;
        margin-top: 3px !important;
    }

    .select-field {
        width: 45px;

        ::v-deep .v-input__slot {
            height: 40px;
            border: none !important;
            background: transparent !important;
            padding: 0 !important;
            text-align: center;
        }
    }

    .items-per-page, .total-items {
        height: 40px;
        padding: 0 12px;
    }

    .total-items {
        flex: 1;
    }

    .select-page {
        padding: 0 12px;
        border-right: 1px solid #DADFEC;
        border-left: 1px solid #DADFEC;
    }

    .btn-prev, .btn-next {
        width: 40px;
        height: 39px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }

    .btn-next {
        background: #FFFFFF;
    }
}

// style for dialog column
.DialogColumns {
    background: #FFFFFF;
    border-radius: 5px !important;
    overflow: hidden;

    .item-config {
        display: flex;
        align-items: center;
        padding: 0 12px;
        height: 48px;
        border-bottom: 1px solid #E4EDFA;
    }

    ::v-deep {
        .v-input--selection-controls {
            margin-top: 20px !important;
        }
    }
}

::v-deep {
    table.v-table thead {
        tr {
            background: #F4F7FE;
            height: 42px;
        }

        th {
            text-transform: none;
        }
    }

    table.v-table tbody td {
        height: 42px;
    }

    .theme--light.v-table tbody tr:hover:not(.v-datatable__expand-row) {
        background: #F4F7FE !important;
    }

    .theme--light.v-table tbody tr:not(:first-child) {
        border-top: 1px solid #DADFEC;
    }

    .v-pagination {
        height: 40px;
        display: flex !important;

        ul {
            li:last-child .v-pagination__navigation {
                border: none !important;
            }
        }
    }

    .pager .v-pagination li .v-pagination__navigation, .pager .v-pagination li .v-pagination__item {
        height: 100%;
        min-height: 40px;
        width: 40px;
        margin: 0 !important;
        border: none !important;
        border-right: 1px solid #DADFEC !important;
        border-radius: 0 !important;
        background: #F4F7FE;
        color: #8A97AA !important;
    }

    .pager .v-pagination li .v-pagination__item.v-pagination__item--active {
        background: #FFFFFF !important;
        color: #0052cc !important;
        font-weight: bold;
    }

    .v-pagination__more {
        margin: 0;
        height: 40px !important;
        width: 40px !important;
        align-items: center !important;
        border-right: 1px solid #DADFEC !important;
    }
}
</style>
